import { useMemo } from 'react';

import { isNotEmpty, useForm } from '@mantine/form';

import { Select } from '../../../../Combobox';
import { Alert } from '../../../../Feedback/Alert/Alert';
import { TextInput } from '../../../../Inputs/TextInput';
import { Grid } from '../../../../Layout/Grid/Grid';
import { Group } from '../../../../Layout/Group';
import { Stack } from '../../../../Layout/Stack';
import { Box } from '../../../../Miscellaneous/Box';
import { Title } from '../../../../Typography/Title/Title';

type AddressFormType = {
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
};

type AddressFormValues = {
  asArray: string[];
  asString: string;
  asObject: Record<keyof AddressFormType, string>;
};

const transformAddressValues = (address: AddressFormType): AddressFormValues => {
  const asArray = Object.values(address);
  const asString = asArray.filter((string) => !!string).join(', ');
  const asObject = address;
  return { asArray, asString, asObject };
};

const errorMessages = {
  address1: 'Address is required',
  city: 'City is required',
  state: 'State is required',
  zip_code: 'A valid zip code is required',
};

const AddressForm = ({
  required,
  id,
  onChange,
  useFormValidator,
}: {
  required: boolean;
  id: string;
  onChange?: (address: AddressFormType, addressValueFormats: AddressFormValues) => void;
  useFormValidator?: <TForm extends ReturnType<typeof useForm<any>>>(args: {
    form: TForm;
    id: string;
  }) => void;
}) => {
  const form = useForm<AddressFormType>({
    initialValues: {
      address1: '',
      address2: '',
      city: '',
      state: '',
      zip_code: '',
      country: 'US',
    },
    validate: {
      address1: required ? isNotEmpty('') : undefined,
      city: required ? isNotEmpty('') : undefined,
      state: required ? isNotEmpty('') : undefined,
      country: required ? isNotEmpty('') : undefined,
      zip_code: (value) => (required ? value.length >= 5 === false : undefined),
    },
    validateInputOnBlur: true,
    onValuesChange(values) {
      onChange?.(values, transformAddressValues(values));
    },
  });

  const hasError = useMemo(() => Object.keys(form.errors)[0], [form.errors]);

  useFormValidator?.({ form, id });

  return (
    <Stack>
      <Grid grow gutter={8}>
        <Grid.Col span={12}>
          <TextInput placeholder="Address, Line 1" {...form.getInputProps('address1')} />
        </Grid.Col>
        <Grid.Col span={12}>
          <TextInput placeholder="Address, Line 2" {...form.getInputProps('address2')} />
        </Grid.Col>
        <Grid.Col span={12}>
          <TextInput placeholder="City" {...form.getInputProps('city')} />
        </Grid.Col>
        <Grid.Col span={6}>
          <Select.States {...form.getInputProps('state')} />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput placeholder="Zip" {...form.getInputProps('zip_code')} />
        </Grid.Col>
        <Grid.Col span={12}>
          <Select.Countries {...form.getInputProps('country')} />
        </Grid.Col>
      </Grid>
      <Alert.Transition title={errorMessages[hasError]} type="error" mounted={Boolean(hasError)} />
    </Stack>
  );
};

/** Provides a Wrapper for  AddressForm and allowing dynamic Titles */
const AddressFormWrapper = ({
  required,
  id,
  onChange,
  title = 'Mailing Address',
  useFormValidator,
}: {
  title?: string;
  required: boolean;
  id: string;
  onChange?: (
    address: AddressFormType,
    addressValueFormats: AddressFormValues,
    isValid?: boolean
  ) => void;
  useFormValidator?: <TForm extends ReturnType<typeof useForm<Record<string, unknown>>>>(args: {
    form: TForm;
    id: string;
  }) => void;
}) => {
  return (
    <Stack>
      <Group gap={4}>
        <Title order={4}>{title}</Title>
        {required && (
          <Box c="cerise.7" fz="sm" fw={500}>
            *
          </Box>
        )}
      </Group>
      <AddressForm
        required={required}
        id={id}
        onChange={onChange}
        useFormValidator={useFormValidator}
      />
    </Stack>
  );
};

export type { AddressFormType, AddressFormValues };

export { AddressForm, AddressFormWrapper };
